// src/pages/ClientQuestions/ClientQuestionList.jsx
// -----------------------------------------------------------------------------
// Admin/PM screen for managing Client Questions using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) via clientQuestionService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import clientQuestionService from '../../api/clientQuestionService';
import projectService from '../../api/projectService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

export default function ClientQuestionList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects once for select options and column rendering
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => setProjects([]));
  }, []);

  // Hook for client questions CRUD state
  const {
    data,
    loading,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    totalPages,
    totalRecords,
    pageSize,
    setPageSize,
    paginatedData,
  } = useTableData(clientQuestionService, {
    idKey: 'questionID',
    itemName: 'Client Question',
    initialSort: { key: 'sequenceOrder', direction: 'asc' },
  });

  // Schema configuration for Client_Question_TBL
  const questionFields = useMemo(() => {
    const list = [];

    if (formMode === 'edit') {
      list.push({
        name: 'questionID',
        label: 'Question ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
    }

    list.push(
      {
        name: 'projectID',
        label: 'Project',
        type: 'select',
        required: true,
        options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
      },
      {
        name: 'questionText',
        label: 'Question Text',
        type: 'varchar',
        required: true,
        maxLength: 500,
        placeholder: 'Enter question text details...',
      },
      {
        name: 'isMandatoryFlag',
        label: 'Is Mandatory?',
        type: 'select',
        required: true,
        options: [
          { label: 'Yes (Required)', value: 'true' },
          { label: 'No (Optional)', value: 'false' },
        ],
      },
      {
        name: 'sequenceOrder',
        label: 'Sequence Order',
        type: 'integer',
        required: true,
        min: 0,
        placeholder: 'e.g. 1, 2, 3...',
      }
    );

    return list;
  }, [projects, formMode]);

  // Data grid columns configuration
  const columns = [
    { key: 'questionID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : '-';
      },
    },
    { key: 'questionText', label: 'Question Text', sortable: true },
    {
      key: 'isMandatoryFlag',
      label: 'Mandatory',
      sortable: true,
      render: (row) => {
        const isMandatory = row.isMandatoryFlag === true || String(row.isMandatoryFlag) === 'true';
        return (
          <StatusBadge
            value={isMandatory ? 'Yes' : 'No'}
            customClass={isMandatory ? 'badge-danger' : 'badge-info'}
          />
        );
      },
    },
    { key: 'sequenceOrder', label: 'Sequence Order', sortable: true, width: '150px' },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Client Questions</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Full CRUD tracking for configurable ticket form input questionnaires.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Client Question
        </button>
      </div>

      {/* Main Table view */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="questionID"
        emptyMessage="No questions found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Dialog */}
      {isFormOpen && (
        <TableForm
          fields={questionFields}
          initialValues={selectedRecord || { projectID: '', questionText: '', isMandatoryFlag: 'true', sequenceOrder: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Client Question"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Client Question"
        itemLabel={selectedRecord ? selectedRecord.questionText : 'Question'}
        deleting={deleting}
      />
    </div>
  );
}
